import { prefersReducedMotion, isLowPowerDevice } from '@theme/utilities';

/**
 * Gedeelde scroll-onthuller voor alle Tiny Ties-secties.
 *
 * Waarom dit bestaat: vijf maatwerksecties animeerden met pure CSS die afging
 * zodra de pagina laadde. Alles onder de vouw was daardoor al uitgeanimeerd
 * voordat de bezoeker er was — de animatie werd betaald maar nooit gezien.
 * Twee andere secties hadden elk hun eigen IntersectionObserver, met eigen
 * drempels en eigen tijden.
 *
 * Gebruik in Liquid:
 *
 *   <div data-tt-reveal>…</div>
 *   <div data-tt-reveal data-tt-reveal-delay="2">…</div>   → 2 × stagger
 *   <ul data-tt-reveal-group>                              → kinderen na elkaar
 *     <li data-tt-reveal>…</li>
 *   </ul>
 *
 * De bijbehorende CSS staat in tiny-ties.css onder "Tiny Ties — onthullingen".
 *
 * Een sectie met een eigen, al goedgekeurde choreografie hoeft die niet op te
 * geven om toch mee te liften op deze ene observer:
 *
 *   <div data-tt-observe data-tt-reveal-class="is-visible">…</div>
 *
 * `data-tt-observe` krijgt géén gedeelde CSS — de sectie houdt zijn eigen
 * opmaak en zet zelf de betekenis van zijn klasse. Zo verdwijnt de
 * gedupliceerde observer-JS zonder dat er iets aan het beeld verandert.
 */

const REVEAL_SELECTOR = '[data-tt-reveal], [data-tt-observe]';
const SCROLL_REVEAL_SELECTOR = '[data-tt-reveal]';
const OBSERVE_SELECTOR = '[data-tt-observe]';
const READY_CLASS = 'tt-reveal-ready';
const SCROLL_READY_CLASS = 'tt-scroll-reveal-ready';
const VISIBLE_CLASS = 'tt-in';

/** @type {'idle' | 'scroll' | 'observer'} */
let revealMode = 'idle';

/**
 * De klasse die dit element bij onthulling krijgt.
 * @param {Element} element
 * @returns {string}
 */
function visibleClassFor(element) {
  return element.getAttribute('data-tt-reveal-class') || VISIBLE_CLASS;
}

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isRevealed(element) {
  return element.classList.contains(visibleClassFor(element));
}

/**
 * @param {Element} element
 */
function reveal(element) {
  element.classList.add(visibleClassFor(element));
}

/** Onder deze drempel telt een element als "in beeld". */
const THRESHOLD = 0.15;

/** Iets vóór de onderrand starten, zodat de beweging al loopt bij binnenkomst. */
const ROOT_MARGIN = '0px 0px -12% 0px';

/**
 * Vangnet. Als een observer om welke reden dan ook niet afvuurt — een
 * container met `overflow: hidden`, een sectie die buiten beeld wordt
 * gerenderd, een browserfout — dan mag content nooit onzichtbaar blijven.
 * Overgenomen uit de bestaande tiny-ties-moments-implementatie.
 */
const FAILSAFE_MS = 1800;

/**
 * Het vangnet mag alleen ingrijpen waar het écht misgaat: content die in of
 * boven het scherm staat en tóch onzichtbaar is. Alles daaronder is niet
 * "gemist" maar simpelweg nog niet bereikt.
 *
 * Waarom dit onderscheid er moet zijn: een vangnet dat onvoorwaardelijk
 * alles toonde, maakte de hele observer zinloos. Gemeten op de homepage
 * vuurden alle acht onthullingen tegelijk op 1,85 s zonder dat er ook maar
 * één pixel was gescrold — terwijl ze 1,3 tot 4,3 schermen onder de vouw
 * lagen. De bezoeker kreeg de animatie dus nooit te zien; precies de
 * kwaal die dit systeem moest verhelpen.
 *
 * Marge onder de onderrand, zodat een element dat net binnenkomt nog als
 * "hoort zichtbaar te zijn" telt.
 */
const FAILSAFE_SLACK_PX = 200;

let observer = null;
let failsafeTimer = null;

/**
 * Toont alles onmiddellijk, zonder animatie. Alleen voor het geval dat we
 * überhaupt niet animeren (reduced motion, low-power, geen observer).
 * @param {ParentNode} [root]
 */
function revealAll(root = document) {
  for (const element of root.querySelectorAll(REVEAL_SELECTOR)) {
    reveal(element);
  }
}

/**
 * Toont uitsluitend wat in of boven beeld staat en toch nog verborgen is.
 * Laat de observer intact, zodat de rest gewoon bij het scrollen aan de
 * beurt komt.
 */
function revealMissed() {
  const limit = window.innerHeight + FAILSAFE_SLACK_PX;

  for (const element of document.querySelectorAll(REVEAL_SELECTOR)) {
    if (isRevealed(element)) continue;
    if (element.getBoundingClientRect().top > limit) continue;

    reveal(element);
    observer?.unobserve(element);
  }
}

/** Zet het vangnet (opnieuw) klaar. */
function armFailsafe() {
  window.clearTimeout(failsafeTimer);
  failsafeTimer = window.setTimeout(revealMissed, FAILSAFE_MS);
}

/**
 * Bepaalt of we überhaupt mogen animeren. IntersectionObserver is alleen
 * nodig voor het vangnet; moderne browsers sturen de gedeelde onthullingen
 * rechtstreeks vanuit CSS aan.
 * @returns {boolean}
 */
function motionAllowed() {
  return !prefersReducedMotion() && !isLowPowerDevice();
}

/**
 * Controleert beide benodigde declaraties, zodat een gedeeltelijke
 * implementatie geen content kan verbergen die de browser niet onthult.
 * @returns {boolean}
 */
function supportsScrollDrivenAnimations() {
  return (
    typeof CSS !== 'undefined' &&
    CSS.supports?.('animation-timeline: view()') === true &&
    CSS.supports?.('animation-range: entry 0% cover 26%') === true
  );
}

/**
 * Zet de trapsgewijze vertraging als CSS-variabele, zodat de CSS de tokens
 * uit theme-styles-variables gebruikt in plaats van harde millisecondes.
 * @param {Element} element
 * @param {number} steps
 */
function applyStagger(element, steps) {
  if (steps <= 0) return;

  element.style.setProperty('--tt-reveal-step', String(steps));

  if (revealMode === 'scroll' && element.matches(SCROLL_REVEAL_SELECTOR)) {
    const offset = Math.min(steps * 2, 12);
    element.style.setProperty('--tt-reveal-entry', `${offset}%`);
    element.style.setProperty('--tt-reveal-cover', `${26 + offset}%`);
  }
}

/**
 * Geeft elk kind binnen een groep een oplopende vertraging.
 * @param {Element} group
 */
function indexGroup(group) {
  const items = group.querySelectorAll(REVEAL_SELECTOR);
  items.forEach((item, index) => {
    if (!item.hasAttribute('data-tt-reveal-delay')) applyStagger(item, index);
  });
}

/**
 * Neemt nieuwe elementen op — ook die na een sectie-herrendering ontstaan.
 * @param {ParentNode} [root]
 */
export function observeReveals(root = document) {
  for (const group of root.querySelectorAll('[data-tt-reveal-group]')) {
    indexGroup(group);
  }

  if (revealMode === 'scroll') {
    for (const element of root.querySelectorAll(`${SCROLL_REVEAL_SELECTOR}[data-tt-reveal-delay]`)) {
      const explicitDelay = Number(element.getAttribute('data-tt-reveal-delay'));
      if (Number.isFinite(explicitDelay)) applyStagger(element, explicitDelay);
    }
  }

  const selector = revealMode === 'scroll' ? OBSERVE_SELECTOR : REVEAL_SELECTOR;

  for (const element of root.querySelectorAll(selector)) {
    if (isRevealed(element)) continue;

    const explicitDelay = Number(element.getAttribute('data-tt-reveal-delay'));
    if (Number.isFinite(explicitDelay)) applyStagger(element, explicitDelay);

    if (observer) observer.observe(element);
    else reveal(element);
  }
}

function init() {
  // Zonder animatie: niets verbergen, niets observeren. Geen ready-klasse
  // betekent dat de CSS alle content laat staan.
  if (!motionAllowed()) {
    revealAll();
    return;
  }

  revealMode = supportsScrollDrivenAnimations() ? 'scroll' : 'observer';

  // Pas verbergen nadat JS bevestigt dat de actieve route content kan tonen.
  // Zet JS uit of faalt het bestand, dan blijft alle content zichtbaar.
  document.documentElement.classList.add(revealMode === 'scroll' ? SCROLL_READY_CLASS : READY_CLASS);

  if (typeof IntersectionObserver === 'function') {
    observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          reveal(entry.target);
          observer?.unobserve(entry.target);
        }
      },
      { threshold: THRESHOLD, rootMargin: ROOT_MARGIN }
    );
  }

  observeReveals();

  // CSS stuurt [data-tt-reveal] in moderne browsers aan. De observer en het
  // positiebewuste vangnet blijven daar alleen actief voor [data-tt-observe],
  // de secties met een eigen choreografie.
  if (observer) armFailsafe();

  // Bij elke scrollbeweging opnieuw klaarzetten. Niet `once`: het vangnet moet
  // ook ná de eerste scroll blijven meelopen, want pas dan komen de meeste
  // secties in beeld — en het mag nooit één keer alles ineens tonen.
  if (observer) document.addEventListener('scroll', armFailsafe, { passive: true });

  // Shopify hertekent secties in de theme-editor en bij dynamische updates.
  document.addEventListener('shopify:section:load', (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target) observeReveals(target);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
